import { v4 as uuidv4 } from 'uuid';

import {
    addFieldsToArray,
    buildField,
    getFieldValueIfDirty,
    removeFromArray,
    updateTabField,
    updateTabFieldArray
} from '../../common/utils';
import {
    validatePacketFwdAddress,
    validatePacketFwdDuplications,
    validatePacketFwdProtocolsLimit
} from '../../common/validators';

import { SUBCATEGORIES } from '../../common/categories';
import {
    COMPANY_INFO_FIELDS,
    FAIL_CLOSE_FIELDS,
    IS_NEW_FIELD,
    PACKET_FWD_ADDRESS_FIELDS,
    RADIUS_FIELDS,
    SELECTIVE_FWD_FIELDS,
    SMTP_SERVER_FIELDS
} from '../../common/fields';
import * as actions from './generalActions';
import { commonActionsReducer, DISCARD } from '../../common/actions';
import {
    AUTH_LOGS_MAX_DOCS_TO_SEARCH_LIMIT,
    PACKET_FWD_ADDRESS_OPTIONS,
    PACKET_FWD_PROTOCOL_OPTIONS,
    PACKET_FWD_TYPE_OPTIONS
} from '@silverfort-nodelibs/common/utils/consts';
import { SYSTEM_MODES } from '@silverfort-nodelibs/common/utils/system-mode-config';

const { COMPANY_INFO, RADIUS_CLIENTS, SMTP_SERVER, FAIL_CLOSE, SELECTIVE_FORWARDING } = SUBCATEGORIES;

const {
    COMPANY_NAME,
    COMPANY_LOGO,
    COMPANY_SUPPORT_EMAIL,
    SNMP_ACCOUNT,
    WINDOWS_LOGON_TOKEN,
    CLEAN_AUTH_LOGS,
    MAX_AUTH_AGG_THRESHOLD,
    AUTO_DENY_MFA_THRESHOLD,
    AUTO_DENY_MFA_WINDOW_SIZE
} = COMPANY_INFO_FIELDS;
const { IP_ADDRESS, SECRET, ENABLE_RADIUS_OTP, DISABLE_FIRST_FACTOR, ID: RADIUS_ID } = RADIUS_FIELDS;
const { SMTP_SERVER_URL, SMTP_FROM_ADDRESS, SMTP_ACCOUNT, SMTP_ENABLED } = SMTP_SERVER_FIELDS;
const { NON_AD_PROTOCOLS, AD_PROTOCOLS } = FAIL_CLOSE_FIELDS;
const { SELECTIVE_FORWARDING: SELECTIVE_FORWARDING_FIELD, EXCLUDE_EXCHANGE_SERVERS } = SELECTIVE_FWD_FIELDS;

export const fieldsReducer = (state, action) => {
    switch (action.type) {
        case actions.UPDATE_COMPANY_INFO:
            return updateTabField(state, COMPANY_INFO.ID, action.payload.fieldId, action.payload.value);
        case actions.UPDATE_RADIUS_CLIENT:
            return updateTabFieldArray(
                state,
                RADIUS_CLIENTS.ID,
                action.payload.fieldId,
                action.payload.index,
                action.payload.arrFieldId,
                action.payload.value
            );
        case actions.ADD_RADIUS_CLIENT:
            return addFieldsToArray(state, RADIUS_CLIENTS.ID, RADIUS_CLIENTS.ID, newRadiusClient());
        case actions.REMOVE_RADIUS_CLIENT:
            return removeFromArray(state, RADIUS_CLIENTS.ID, RADIUS_CLIENTS.ID, action.payload);
        case actions.UPDATE_SMTP_SERVER:
            return updateTabField(state, SMTP_SERVER.ID, action.payload.fieldId, action.payload.value);
        case actions.UPDATE_FAIL_CLOSE_FIELD:
            if (action.payload.fieldId === AD_PROTOCOLS.ID) {
                return updatePacketFwdField(state, action.payload, FAIL_CLOSE.ID);
            } else {
                return updateTabField(state, FAIL_CLOSE.ID, action.payload.fieldId, action.payload.value);
            }
        case actions.ADD_FAIL_CLOSE_VALUE:
            const newFailClose = mapPacketFwdFields(
                [
                    {
                        protocols: [
                            PACKET_FWD_PROTOCOL_OPTIONS.ntlm.value,
                            PACKET_FWD_PROTOCOL_OPTIONS.ldap.value,
                            PACKET_FWD_PROTOCOL_OPTIONS.ldaps.value
                        ],
                        type: PACKET_FWD_TYPE_OPTIONS.dest.value,
                        addressType: PACKET_FWD_ADDRESS_OPTIONS.subnet.value,
                        rangeStart: '',
                        rangeEnd: '',
                        subnets: [],
                        note: ''
                    }
                ],
                'Fail-Close',
                true
            );
            return addFieldsToArray(state, FAIL_CLOSE.ID, AD_PROTOCOLS.ID, newFailClose[0]);
        case actions.REMOVE_FAIL_CLOSE_VALUE:
            const FCRemovedState = removeFromArray(state, FAIL_CLOSE.ID, AD_PROTOCOLS.ID, action.payload);
            validatePacketFwdProtocolsLimit(
                FCRemovedState[FAIL_CLOSE.ID],
                FCRemovedState[SELECTIVE_FORWARDING.ID],
                FCRemovedState[FAIL_CLOSE.ID].validators.maxAddressesPerProtocol
            );
            return FCRemovedState;
        case actions.UPDATE_SELECTIVE_FWD_FIELD:
            return updatePacketFwdField(state, action.payload, SELECTIVE_FORWARDING.ID);
        case actions.ADD_SELECTIVE_FWD_VALUE:
            const newSelectiveFwd = mapPacketFwdFields(
                [
                    {
                        protocols: [
                            PACKET_FWD_PROTOCOL_OPTIONS.ntlm.value,
                            PACKET_FWD_PROTOCOL_OPTIONS.ldap.value,
                            PACKET_FWD_PROTOCOL_OPTIONS.ldaps.value
                        ],
                        type: PACKET_FWD_TYPE_OPTIONS.dest.value,
                        addressType: PACKET_FWD_ADDRESS_OPTIONS.subnet.value,
                        rangeStart: '',
                        rangeEnd: '',
                        subnets: [],
                        note: ''
                    }
                ],
                'Selective-Forwarding',
                true
            );
            return addFieldsToArray(state, SELECTIVE_FORWARDING.ID, SELECTIVE_FORWARDING_FIELD.ID, newSelectiveFwd[0]);
        case actions.REMOVE_SELECTIVE_FWD_VALUE:
            const SFRemovedState = removeFromArray(
                state,
                SELECTIVE_FORWARDING.ID,
                SELECTIVE_FORWARDING_FIELD.ID,
                action.payload
            );
            validatePacketFwdProtocolsLimit(
                SFRemovedState[FAIL_CLOSE.ID],
                SFRemovedState[SELECTIVE_FORWARDING.ID],
                SFRemovedState[FAIL_CLOSE.ID].validators.maxAddressesPerProtocol
            );
            return SFRemovedState;
        case actions.SWITCH_EXCHANGE_SERVERS:
            return updateTabField(
                state,
                SELECTIVE_FORWARDING.ID,
                EXCLUDE_EXCHANGE_SERVERS.ID,
                !state[SELECTIVE_FORWARDING.ID].fields[EXCLUDE_EXCHANGE_SERVERS.ID].value
            );
        case DISCARD:
            const discardState = commonActionsReducer(state, action, initFieldsState);
            validatePacketFwdProtocolsLimit(
                discardState[FAIL_CLOSE.ID],
                discardState[SELECTIVE_FORWARDING.ID],
                discardState[FAIL_CLOSE.ID].validators.maxAddressesPerProtocol
            );
            return discardState;
        default:
            return commonActionsReducer(state, action, initFieldsState);
    }
};

const updatePacketFwdField = (state, payload, tabId) => {
    let newState = updateTabFieldArray(state, tabId, payload.fieldId, payload.index, payload.arrFieldId, payload.value);
    if (payload.arrFieldId === PACKET_FWD_ADDRESS_FIELDS.TYPE.ID) {
        let newValue = [];
        if (payload.value === PACKET_FWD_TYPE_OPTIONS.source.value) {
            newValue = [PACKET_FWD_PROTOCOL_OPTIONS.kerberos.value];
        } else {
            newValue = Object.values(PACKET_FWD_PROTOCOL_OPTIONS)
                .map((protocol) => protocol.value)
                .filter((protocol) => protocol !== PACKET_FWD_PROTOCOL_OPTIONS.kerberos.value);
        }
        newState = updateTabFieldArray(
            newState,
            tabId,
            payload.fieldId,
            payload.index,
            PACKET_FWD_ADDRESS_FIELDS.PROTOCOL.ID,
            newValue
        );
    } else if (
        payload.arrFieldId === PACKET_FWD_ADDRESS_FIELDS.SUBNET.ID ||
        payload.arrFieldId === PACKET_FWD_ADDRESS_FIELDS.RANGE_START.ID ||
        payload.arrFieldId === PACKET_FWD_ADDRESS_FIELDS.RANGE_END.ID
    ) {
        validatePacketFwdAddress([newState[tabId].fields[payload.fieldId].value[payload.index]]);
    }
    validatePacketFwdDuplications(newState[tabId].fields[payload.fieldId].value);
    validatePacketFwdProtocolsLimit(
        newState[FAIL_CLOSE.ID],
        newState[SELECTIVE_FORWARDING.ID],
        newState[FAIL_CLOSE.ID].validators.maxAddressesPerProtocol
    );
    return newState;
};

export const initFieldsState = ({ settings }) => {
    const systemMode = settings.systemMode || SYSTEM_MODES.ON_PREM;
    const isCompanyNameRequired = systemMode === SYSTEM_MODES.ON_PREM;

    return {
        [COMPANY_INFO.ID]: {
            id: COMPANY_INFO.ID,
            name: COMPANY_INFO.NAME,
            fields: {
                [COMPANY_NAME.ID]: buildField(COMPANY_NAME, settings.CompanyName, { required: isCompanyNameRequired }),
                [COMPANY_LOGO.ID]: buildField(COMPANY_LOGO, settings.CompanyLogo),
                [COMPANY_SUPPORT_EMAIL.ID]: buildField(COMPANY_SUPPORT_EMAIL, settings.CompanySupportEmail, {
                    email: true
                }),
                [SNMP_ACCOUNT.ID]: buildField(SNMP_ACCOUNT, { user: settings.SNMPUsername, pass: '' }),
                [WINDOWS_LOGON_TOKEN.ID]: buildField(WINDOWS_LOGON_TOKEN, settings.DesktopMFAClientSecret),
                [CLEAN_AUTH_LOGS.ID]: buildField(CLEAN_AUTH_LOGS, settings.CleanAutenticationsLogsAfterXWeeks, {
                    isNumeric: true,
                    minValue: 2,
                    maxValue: 52
                }),
                [AUTO_DENY_MFA_THRESHOLD.ID]: buildField(AUTO_DENY_MFA_THRESHOLD, settings.AutoDenyMfaThreshold, {
                    isNumeric: true
                }),
                [AUTO_DENY_MFA_WINDOW_SIZE.ID]: buildField(AUTO_DENY_MFA_WINDOW_SIZE, settings.AutoDenyMfaWindowSize, {
                    isNumeric: true
                }),
                [MAX_AUTH_AGG_THRESHOLD.ID]: buildField(MAX_AUTH_AGG_THRESHOLD, settings.AuthLogsAggsMaxDocsToSearch, {
                    isNumeric: true,
                    required: true,
                    minValue: 1,
                    maxValue: AUTH_LOGS_MAX_DOCS_TO_SEARCH_LIMIT
                })
            },
            validators: {
                adaptiveBlock: [AUTO_DENY_MFA_THRESHOLD.ID, AUTO_DENY_MFA_WINDOW_SIZE.ID]
            }
        },
        [RADIUS_CLIENTS.ID]: {
            id: RADIUS_CLIENTS.ID,
            name: RADIUS_CLIENTS.NAME,
            fields: {
                [RADIUS_CLIENTS.ID]: buildField(RADIUS_CLIENTS, mapRadiusClientsFields(settings.RADIUSClients))
            },
            validators: {
                radiusClientOverlapping: [RADIUS_CLIENTS.ID, IP_ADDRESS.ID]
            }
        },
        [SMTP_SERVER.ID]: {
            id: SMTP_SERVER.ID,
            name: SMTP_SERVER.NAME,
            fields: {
                [SMTP_ENABLED.ID]: buildField(SMTP_ENABLED, settings.SMTPEnabled),
                [SMTP_SERVER_URL.ID]: buildField(SMTP_SERVER_URL, settings.SMTPServerURL, { notWhitespace: true }),
                [SMTP_FROM_ADDRESS.ID]: buildField(SMTP_FROM_ADDRESS, settings.SMTPFrom, { notWhitespace: true }),
                [SMTP_ACCOUNT.ID]: buildField(SMTP_ACCOUNT, { user: settings.SMTPUsername, pass: '' })
            },
            validators: {},
            error: null
        },
        [FAIL_CLOSE.ID]: {
            id: FAIL_CLOSE.ID,
            name: FAIL_CLOSE.NAME,
            fields: {
                [NON_AD_PROTOCOLS.ID]: buildField(NON_AD_PROTOCOLS, settings.FailsafeMechanism !== 'fail-open'),
                [AD_PROTOCOLS.ID]: buildField(AD_PROTOCOLS, mapPacketFwdFields(settings.FailCloseConfig, 'Fail-Close'))
            },
            isDirty: false,
            error: null,
            validators: {
                packetFwdValidations: AD_PROTOCOLS.ID,
                maxAddressesPerProtocol: settings.ProtocolAddressesLimitInPacketFwdSettings
            }
        },
        [SELECTIVE_FORWARDING.ID]: {
            id: SELECTIVE_FORWARDING.ID,
            name: SELECTIVE_FORWARDING.NAME,
            fields: {
                [SELECTIVE_FORWARDING_FIELD.ID]: buildField(
                    SELECTIVE_FORWARDING_FIELD,
                    mapPacketFwdFields(settings.SelectiveFwdConfig, 'Selective-Forwarding', false)
                ),
                [EXCLUDE_EXCHANGE_SERVERS.ID]: buildField(EXCLUDE_EXCHANGE_SERVERS, settings.excludeExchangeServers)
            },
            isDirty: false,
            error: null,
            validators: {
                packetFwdValidations: SELECTIVE_FORWARDING_FIELD.ID,
                maxAddressesPerProtocol: settings.ProtocolAddressesLimitInPacketFwdSettings
            }
        }
    };
};

export const mapRadiusClientsFields = (radiusClients) => {
    if (!radiusClients) {
        return [];
    }

    return radiusClients.map((rc) => ({
        [IP_ADDRESS.ID]: buildField(IP_ADDRESS, rc.ipAddress, { required: true, ipAndMask: true }),
        [SECRET.ID]: buildField(SECRET, rc.encKey, { required: true }),
        [ENABLE_RADIUS_OTP.ID]: buildField(ENABLE_RADIUS_OTP, rc.enableOTP),
        [DISABLE_FIRST_FACTOR.ID]: buildField(DISABLE_FIRST_FACTOR, rc.disableFirstFactor),
        [IS_NEW_FIELD.ID]: buildField(IS_NEW_FIELD, rc.isNew),
        [RADIUS_ID.ID]: buildField(RADIUS_ID, rc.id)
    }));
};

export const mapFieldsToRadiusClients = (radiusClientsFields) => {
    const radiusClients = [];
    for (const radius of radiusClientsFields) {
        radiusClients.push({
            ipAddress: radius[IP_ADDRESS.ID].value,
            encKey: radius[SECRET.ID].value,
            enableOTP: radius[ENABLE_RADIUS_OTP.ID].value,
            disableFirstFactor: radius[DISABLE_FIRST_FACTOR.ID].value,
            id: radius[RADIUS_ID.ID].value
        });
    }

    return radiusClients;
};

export const newRadiusClient = () => {
    const newRadiusClient = mapRadiusClientsFields([
        {
            ipAddress: '',
            encKey: uuidv4().replace(/-/g, '').substring(0, 24),
            isNew: true,
            id: null
        }
    ]);
    return newRadiusClient[0];
};

const mapPacketFwdFields = (packetFwdConfig, descTypeText, isNew = false) => {
    const { ADDRESS_TYPE, RANGE_START, RANGE_END, SUBNET, PROTOCOL, TYPE, NOTES } = PACKET_FWD_ADDRESS_FIELDS;
    const packetFwdFields = [];

    const subnetField = { ...SUBNET, DESC: SUBNET.DESC.replace('{packet-fwd-type}', descTypeText) };
    const rangeStartField = { ...RANGE_START, DESC: RANGE_START.DESC.replace('{packet-fwd-type}', descTypeText) };
    for (const packetFwdObj of packetFwdConfig) {
        packetFwdFields.push({
            [ADDRESS_TYPE.ID]: buildField(ADDRESS_TYPE, packetFwdObj.addressType),
            [RANGE_START.ID]: buildField(rangeStartField, packetFwdObj.rangeStart, { ip: true }),
            [RANGE_END.ID]: buildField(RANGE_END, packetFwdObj.rangeEnd, { ip: true }),
            [SUBNET.ID]: buildField(subnetField, packetFwdObj.subnets || [], { ipsOrIpAndMasks: true }),
            [PROTOCOL.ID]: buildField(PROTOCOL, packetFwdObj.protocols, { required: true }),
            [TYPE.ID]: buildField(TYPE, packetFwdObj.type, { required: true }),
            [NOTES.ID]: buildField(NOTES, packetFwdObj.note || ''),
            [IS_NEW_FIELD.ID]: buildField(IS_NEW_FIELD, isNew)
        });
    }

    return packetFwdFields;
};

export const mapFieldsToPacketFwd = (packerFwdFields) => {
    const packetFwdConfig = [];
    const { ADDRESS_TYPE, RANGE_START, RANGE_END, SUBNET, PROTOCOL, TYPE, NOTES } = PACKET_FWD_ADDRESS_FIELDS;

    for (const packetFwdField of packerFwdFields) {
        const addressType = packetFwdField[ADDRESS_TYPE.ID].value;
        const isRange = addressType === PACKET_FWD_ADDRESS_OPTIONS.range.value;
        packetFwdConfig.push({
            addressType: addressType,
            rangeStart: isRange ? packetFwdField[RANGE_START.ID].value : '',
            rangeEnd: isRange ? packetFwdField[RANGE_END.ID].value : '',
            subnets: !isRange ? packetFwdField[SUBNET.ID].value : null,
            protocols: packetFwdField[PROTOCOL.ID].value,
            type: packetFwdField[TYPE.ID].value,
            note: packetFwdField[NOTES.ID].value
        });
    }

    return packetFwdConfig;
};

export const mapToSettings = (saveAll, selectedTab, fields) => {
    const newSettings = {};
    if (saveAll || selectedTab === COMPANY_INFO.ID) {
        const companyInfoFields = fields[COMPANY_INFO.ID].fields;
        newSettings.CompanyName = getFieldValueIfDirty(companyInfoFields[COMPANY_NAME.ID]);
        newSettings.CompanyLogo = getFieldValueIfDirty(companyInfoFields[COMPANY_LOGO.ID]);
        newSettings.CompanySupportEmail = getFieldValueIfDirty(companyInfoFields[COMPANY_SUPPORT_EMAIL.ID]);
        newSettings.SNMPUsername = getFieldValueIfDirty(
            companyInfoFields[SNMP_ACCOUNT.ID],
            companyInfoFields[SNMP_ACCOUNT.ID].value.user
        );
        newSettings.SNMPPassword = getFieldValueIfDirty(
            companyInfoFields[SNMP_ACCOUNT.ID],
            companyInfoFields[SNMP_ACCOUNT.ID].value.pass
        );
        newSettings.DesktopMFAClientSecret = getFieldValueIfDirty(companyInfoFields[WINDOWS_LOGON_TOKEN.ID]);
        newSettings.CleanAutenticationsLogsAfterXWeeks = getFieldValueIfDirty(
            companyInfoFields[CLEAN_AUTH_LOGS.ID],
            parseInt(companyInfoFields[CLEAN_AUTH_LOGS.ID].value)
        );
        newSettings.AutoDenyMfaThreshold = getFieldValueIfDirty(companyInfoFields[AUTO_DENY_MFA_THRESHOLD.ID]);
        newSettings.AutoDenyMfaWindowSize = getFieldValueIfDirty(companyInfoFields[AUTO_DENY_MFA_WINDOW_SIZE.ID]);
        newSettings.AuthLogsAggsMaxDocsToSearch = getFieldValueIfDirty(
            companyInfoFields[MAX_AUTH_AGG_THRESHOLD.ID],
            parseInt(companyInfoFields[MAX_AUTH_AGG_THRESHOLD.ID].value)
        );
    }

    if (saveAll || selectedTab === RADIUS_CLIENTS.ID) {
        const radiusClientsFields = fields[RADIUS_CLIENTS.ID].fields;
        newSettings.RADIUSClients = getFieldValueIfDirty(
            radiusClientsFields[RADIUS_CLIENTS.ID],
            mapFieldsToRadiusClients(radiusClientsFields[RADIUS_CLIENTS.ID].value)
        );
    }
    if (saveAll || selectedTab === SMTP_SERVER.ID) {
        const smtpServerFields = fields[SMTP_SERVER.ID].fields;
        newSettings.SMTPEnabled = getFieldValueIfDirty(smtpServerFields[SMTP_ENABLED.ID]);
        newSettings.SMTPServerURL = getFieldValueIfDirty(smtpServerFields[SMTP_SERVER_URL.ID]);
        newSettings.SMTPFrom = getFieldValueIfDirty(smtpServerFields[SMTP_FROM_ADDRESS.ID]);
        newSettings.SMTPUsername = getFieldValueIfDirty(
            smtpServerFields[SMTP_ACCOUNT.ID],
            smtpServerFields[SMTP_ACCOUNT.ID].value.user
        );
        newSettings.SMTPPassword = getFieldValueIfDirty(
            smtpServerFields[SMTP_ACCOUNT.ID],
            smtpServerFields[SMTP_ACCOUNT.ID].value.pass
        );
    }
    if (saveAll || selectedTab === FAIL_CLOSE.ID) {
        const failCloseFields = fields[FAIL_CLOSE.ID].fields;
        newSettings.FailCloseConfig = getFieldValueIfDirty(
            failCloseFields[AD_PROTOCOLS.ID],
            mapFieldsToPacketFwd(failCloseFields[AD_PROTOCOLS.ID].value)
        );

        newSettings.FailsafeMechanism = getFieldValueIfDirty(
            failCloseFields[NON_AD_PROTOCOLS.ID],
            failCloseFields[NON_AD_PROTOCOLS.ID].value ? 'fail-close' : 'fail-open'
        );
    }
    if (saveAll || selectedTab === SELECTIVE_FORWARDING.ID) {
        const selectiveFwdFields = fields[SELECTIVE_FORWARDING.ID].fields;
        newSettings.SelectiveFwdConfig = getFieldValueIfDirty(
            selectiveFwdFields[SELECTIVE_FORWARDING_FIELD.ID],
            mapFieldsToPacketFwd(selectiveFwdFields[SELECTIVE_FORWARDING_FIELD.ID].value)
        );
        newSettings.excludeExchangeServers = selectiveFwdFields[EXCLUDE_EXCHANGE_SERVERS.ID].value;
    }
    return newSettings;
};
